import { BrowserAuthError, BrowserAuthErrorCodes } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { BDPageSpinner } from '@brightdrop/bd-ui';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { clearPersistedProfileSessionData, persistor } from '~/app/store';
import { clearStored } from '~/common/localization/languageOverride/overrideStore';
import { AppRoutePaths } from '~/common/models/route.model';
import {
  clearHeapEventProperties,
  resetHeapIdentity,
} from '~/common/utils/analytics/heap.utils';
import { clearUserTelemetryContext } from '~/common/utils/analytics/login-telemetry';
import { InactivityStorageKeys } from '~/hooks/useInactivityTracker/InactivityTracker.constants';

const Logout = (): JSX.Element => {
  const { instance, inProgress } = useMsal();
  const { search } = useLocation();
  const isLoggingOutRef = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (inProgress !== 'none' || isLoggingOutRef.current) {
      return;
    }

    isLoggingOutRef.current = true;

    const runLogout = async () => {
      try {
        resetHeapIdentity();
        clearHeapEventProperties();
        clearUserTelemetryContext();
        // The console language lives in sessionStorage, which outlasts the sign-in redirect, so the
        // next user to sign in on this tab would otherwise start in it.
        clearStored();
        //Clear the shared cross-tab inactivity timestamp so a later re-login
        //starts a fresh idle window instead of inheriting a stale one.
        //Best-effort: storage may be blocked, and logout must proceed regardless.
        try {
          localStorage.removeItem(InactivityStorageKeys.userLastActivity);
        } catch {
          // Ignore storage failures so logout can continue.
        }
        await clearPersistedProfileSessionData();
        await persistor.purge();
        await instance.handleRedirectPromise();
        const path =
          instance.getConfiguration().auth.postLogoutRedirectUri ?? '';
        const url = new URL(path, window.location.origin);
        const searchParams = new URLSearchParams(search);

        for (const [key, value] of searchParams.entries()) {
          url.searchParams.set(key, value);
        }

        await instance.logoutRedirect({
          postLogoutRedirectUri: url.toString(),
        });
      } catch (e) {
        const isInteractionInProgress =
          e instanceof BrowserAuthError &&
          e.errorCode === BrowserAuthErrorCodes.interactionInProgress;

        if (isInteractionInProgress) {
          isLoggingOutRef.current = false;
          return;
        }

        console.error('Logout error - logoutRedirect', e);

        //Best-effort cleanup: storage may be blocked/unavailable, so guard the
        //loop to ensure the navigation fallback below still runs.
        try {
          for (const key of Object.keys(localStorage)) {
            if (!key.startsWith('batch-upload')) {
              localStorage.removeItem(key);
            }
          }
        } catch (storageError) {
          console.error('Logout error - clearing localStorage', storageError);
        }

        isLoggingOutRef.current = false;
        navigate({
          pathname: AppRoutePaths.LOGIN,
          search,
        });
      }
    };

    runLogout();
  }, [navigate, inProgress, instance, search]);

  return <BDPageSpinner />;
};

export default Logout;
